// PlatformConfig.jsx
//
// Super-admin-only page at /admin/platform-config. One screen to
// manage every environment variable the platform reads — across both
// the app and the worker. Stores in PlatformConfig (Mongo), boot
// loader overlays onto process.env at startup, "Restart services"
// button shells systemctl to apply restart-required changes.
//
// Layout: left rail = sections (LLM / Stripe / Alerts / SMTP /
// Storage / per-platform OAuth / Schedulers / Infra). Main panel =
// the active section's vars with edit-in-place rows. Top right =
// "Restart services" button + "Last refreshed" + dirty indicator
// when one or more rows have unsaved changes.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { get, post, putGeneral, remove } from '../../api';
import addNotification from 'helpers/notify';

function fmtRelative(d) {
  if (!d) return '—';
  const ms = Date.now() - new Date(d).getTime();
  if (isNaN(ms)) return '—';
  const sec = Math.round(ms / 1000);
  if (sec < 60)   return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60)   return `${min}m ago`;
  const hr  = Math.round(min / 60);
  if (hr  < 24)   return `${hr}h ago`;
  return new Date(d).toLocaleDateString();
}

function Row({ row, draft, setDraft, onSave, onClear, savingKey }) {
  const isSecret  = !!row.isSecret;
  const hasValue  = !!row._hasValue;
  const readonly  = !!row.readonly;
  const isDirty   = draft != null && draft !== (row.value || '');
  const isSaving  = savingKey === row.key;
  const placeholder = isSecret && hasValue ? '•••••• change' : (row.default ? `default: ${row.default}` : '');

  return (
    <div style={{
      display: 'grid', gridTemplateColumns: '320px 1fr 220px',
      gap: 14, padding: '10px 14px', borderBottom: '1px solid #f3f4f6',
      alignItems: 'center',
    }}>
      <div>
        <div style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontWeight: 600, fontSize: 13, color: '#15172b' }}>
          {row.key}
          {row.restartRequired && (
            <span style={{ marginLeft: 6, fontSize: 9, padding: '1px 5px', borderRadius: 8, background: '#fff4cc', color: '#856404', fontWeight: 600 }}>
              RESTART
            </span>
          )}
          {isSecret && (
            <span style={{ marginLeft: 6, fontSize: 9, padding: '1px 5px', borderRadius: 8, background: '#f3f4f6', color: '#525252', fontWeight: 600 }}>
              SECRET
            </span>
          )}
          {readonly && (
            <span style={{ marginLeft: 6, fontSize: 9, padding: '1px 5px', borderRadius: 8, background: '#fdecea', color: '#a71d2a', fontWeight: 600 }}>
              READ-ONLY
            </span>
          )}
        </div>
        <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
          {row.label || row.key}
        </div>
        {row.description && (
          <div style={{ fontSize: 11, color: '#aaa', marginTop: 2 }}>{row.description}</div>
        )}
      </div>

      <input
        type={isSecret ? 'password' : 'text'}
        value={draft != null ? draft : (row.value || '')}
        onChange={(e) => setDraft(row.key, e.target.value)}
        placeholder={placeholder}
        autoComplete="new-password"
        disabled={readonly}
        style={{
          padding: '6px 10px', fontSize: 13, border: '1px solid #d1d5db',
          borderRadius: 4, fontFamily: isSecret ? 'monospace' : 'inherit',
          background: readonly ? '#fafbfc' : '#fff',
        }} />

      <div style={{ display: 'flex', gap: 6, alignItems: 'center', justifyContent: 'flex-end', fontSize: 11 }}>
        {hasValue && !isDirty && (
          <span style={{ color: '#1e6b3e' }} title={row._updatedAt ? `Updated ${fmtRelative(row._updatedAt)}` : ''}>
            ✓ set · {fmtRelative(row._updatedAt)}
          </span>
        )}
        {isDirty && (
          <>
            <button
              type="button"
              className="acc-btn acc-btn-primary"
              disabled={readonly || isSaving}
              onClick={() => onSave(row.key)}
              style={{ fontSize: 11, padding: '3px 10px' }}>
              {isSaving ? 'Saving…' : 'Save'}
            </button>
          </>
        )}
        {hasValue && !isDirty && !readonly && (
          <button
            type="button"
            className="acc-btn"
            onClick={() => onClear(row.key)}
            style={{ fontSize: 11, padding: '3px 8px', color: '#a71d2a', borderColor: '#fdecea' }}>
            Clear
          </button>
        )}
      </div>
    </div>
  );
}

export default function PlatformConfig() {
  const [sections, setSections] = useState([]);
  const [vars, setVars] = useState([]);
  const [activeSection, setActiveSection] = useState('llm');
  const [drafts, setDrafts] = useState({});  // { key: pending-value }
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [savingKey, setSavingKey] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [restartBusy, setRestartBusy] = useState(false);

  const refresh = useCallback(async () => {
    setRefreshing(true);
    try {
      const [s, v] = await Promise.all([
        get('/api/admin/platform-config/sections'),
        get('/api/admin/platform-config'),
      ]);
      setSections((s && s.data) || []);
      setVars((v && v.data) || []);
      setLastRefresh(new Date());
      setErr(null);
    } catch (e) {
      setErr((e && (e.message || (e.response && e.response.data && e.response.data.error))) || 'Failed to load');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const sectionVars = useMemo(
    () => vars.filter((v) => v.section === activeSection),
    [vars, activeSection],
  );
  const dirtyCount = useMemo(
    () => Object.keys(drafts).filter((k) => drafts[k] !== '' && drafts[k] != null).length,
    [drafts],
  );
  const restartPending = useMemo(() => {
    // Any saved row whose restartRequired flag is true AND was
    // updated since the page loaded.
    // For simplicity show the badge whenever any restart-required
    // var has a value — operator clicks Restart to apply.
    return vars.some((v) => v.restartRequired && v._hasValue);
  }, [vars]);

  const setDraft = (key, value) => setDrafts((d) => ({ ...d, [key]: value }));

  const saveOne = async (key) => {
    if (drafts[key] == null) return;
    setSavingKey(key);
    try {
      await putGeneral(`/api/admin/platform-config/${encodeURIComponent(key)}`, { value: drafts[key] });
      addNotification(`Saved ${key}`, 'success');
      setDrafts((d) => { const n = { ...d }; delete n[key]; return n; });
      await refresh();
    } catch (e) {
      addNotification((e && (e.message || (e.response && e.response.data && e.response.data.error))) || 'Save failed', 'danger');
    } finally { setSavingKey(null); }
  };

  const saveAll = async () => {
    const pending = Object.entries(drafts);
    for (const [key, value] of pending) {
      try {
        await putGeneral(`/api/admin/platform-config/${encodeURIComponent(key)}`, { value });
      } catch (e) {
        addNotification(`Save failed for ${key}: ${(e && e.message) || ''}`, 'danger');
      }
    }
    setDrafts({});
    await refresh();
    addNotification(`Saved ${pending.length} value${pending.length === 1 ? '' : 's'}`, 'success');
  };

  const clearKey = async (key) => {
    if (!window.confirm(`Clear ${key}?`)) return;
    try {
      await remove(`/api/admin/platform-config/${encodeURIComponent(key)}`, {});
      addNotification(`Cleared ${key}`, 'success');
      await refresh();
    } catch (e) {
      addNotification((e && e.message) || 'Clear failed', 'danger');
    }
  };

  const restartServices = async () => {
    if (!window.confirm('Restart prigmarapp + prigmarworker now? This will interrupt all in-flight requests for ~10 seconds.')) return;
    setRestartBusy(true);
    try {
      await post('/api/admin/platform-config/restart', {}, false);
      addNotification('Restart scheduled — app + worker will come back in ~10s. Re-login if needed.', 'success');
    } catch (e) {
      addNotification((e && e.message) || 'Restart failed', 'danger');
    } finally {
      // Don't clear busy — the app is about to drop our connection.
    }
  };

  return (
    <div style={{ display: 'flex', minHeight: 'calc(100vh - 60px)', background: '#fff' }}>
      {/* Section rail */}
      <aside style={{ width: 240, borderRight: '1px solid #eee', padding: '20px 12px', overflowY: 'auto', position: 'sticky', top: 0, alignSelf: 'flex-start', height: 'calc(100vh - 60px)' }}>
        <h3 style={{ margin: '0 0 4px 0', fontSize: 16 }}>Environment Variables</h3>
        <div style={{ fontSize: 11, color: '#888', marginBottom: 14 }}>Super-admin only</div>
        {sections.map((s) => (
          <button
            key={s.key}
            type="button"
            onClick={() => setActiveSection(s.key)}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%',
              textAlign: 'left', padding: '7px 10px', borderRadius: 4, marginBottom: 2,
              border: '1px solid transparent',
              background: activeSection === s.key ? '#15172b' : 'transparent',
              color: activeSection === s.key ? '#fff' : '#15172b',
              cursor: 'pointer', fontSize: 12,
            }}>
            <span>{s.label}</span>
            <span style={{ fontSize: 10, opacity: 0.7, padding: '0 5px', borderRadius: 8,
                           background: activeSection === s.key ? 'rgba(255,255,255,0.18)' : '#f3f4f6' }}>
              {s.count}
            </span>
          </button>
        ))}
      </aside>

      {/* Main panel */}
      <main style={{ flex: 1, padding: '20px 28px', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
          <div>
            <h4 style={{ margin: 0 }}>{(sections.find((s) => s.key === activeSection) || {}).label || activeSection}</h4>
            <div style={{ fontSize: 12, color: '#888' }}>
              {sectionVars.length} variable{sectionVars.length === 1 ? '' : 's'}
              {lastRefresh && <> · refreshed {fmtRelative(lastRefresh)}</>}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {dirtyCount > 0 && (
              <button className="acc-btn acc-btn-primary" onClick={saveAll} disabled={!!savingKey}
                      style={{ fontSize: 12 }}>
                Save all ({dirtyCount})
              </button>
            )}
            <button className="acc-btn" onClick={refresh} disabled={refreshing} style={{ fontSize: 12 }}>
              {refreshing ? 'Refreshing…' : 'Refresh'}
            </button>
            <button
              className="acc-btn"
              onClick={restartServices}
              disabled={restartBusy}
              style={{
                fontSize: 12,
                background: restartPending ? '#fff4cc' : '#fff',
                color: restartPending ? '#856404' : '#15172b',
                borderColor: restartPending ? '#fbbf24' : '#d1d5db',
                fontWeight: 600,
              }}
              title="Shells `systemctl restart prigmarapp prigmarworker`. Applies all restart-required changes platform-wide.">
              {restartBusy ? 'Restarting…' : 'Restart services'}
            </button>
          </div>
        </div>

        {err && <div className="alert alert-warning" style={{ fontSize: 13 }}>{err}</div>}

        {loading ? (
          <div style={{ color: '#888', fontSize: 13, padding: 20 }}>Loading…</div>
        ) : (
          <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 8, overflow: 'hidden' }}>
            <div style={{
              display: 'grid', gridTemplateColumns: '320px 1fr 220px', gap: 14,
              padding: '8px 14px', background: '#fafbfc', borderBottom: '1px solid #eee',
              fontSize: 11, color: '#666', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5,
            }}>
              <div>Key</div>
              <div>Value</div>
              <div style={{ textAlign: 'right' }}>Status</div>
            </div>
            {sectionVars.map((row) => (
              <Row
                key={row.key}
                row={row}
                draft={drafts[row.key]}
                setDraft={setDraft}
                onSave={saveOne}
                onClear={clearKey}
                savingKey={savingKey}
              />
            ))}
            {sectionVars.length === 0 && (
              <div style={{ padding: 30, textAlign: 'center', color: '#888', fontSize: 13 }}>
                No variables declared in this section.
              </div>
            )}
          </div>
        )}

        {/* Reminder note */}
        <div style={{ fontSize: 11, color: '#888', marginTop: 14, lineHeight: 1.5 }}>
          Changes save to the <code>PlatformConfig</code> Mongo collection and overlay onto <code>process.env</code> at the next app + worker boot. Variables marked <strong>RESTART</strong> need the services to bounce before the new value takes effect; click <strong>Restart services</strong> at the top to apply. Secrets are encrypted at rest with AES-256-GCM and never returned to the browser after save — only a "✓ set" indicator and the audit timestamp.
        </div>
      </main>
    </div>
  );
}
